import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Box, Button, Chip, IconButton, Typography } from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Edit from "@mui/icons-material/Edit";
import BusinessCenter from "@mui/icons-material/BusinessCenter";
import Groups from "@mui/icons-material/Groups";
import Place from "@mui/icons-material/Place";
import Flag from "@mui/icons-material/Flag";
import Person from "@mui/icons-material/Person";
import { getDetails } from "../../../services/MissionService";
import { withSnackbar } from "../../../utils/SnackbarHOC";
import Loader from "../../../components/Loader/Loader";
import LabelHtmlToTextCustom from "../../../components/LabelHtmlToTextCustom/LabelHtmlToTextCustom";
import MissionAiProposals from "./MissionAiProposals";
import "./visualisermission.css";

const normalizeValue = (value) => {
  if (Array.isArray(value)) return value.length ? value.filter(Boolean).join(" - ") : "-";
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const formattedString = String(dateString).split(" ")[0];
  const date = new Date(formattedString);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const DetailList = ({ items }) => (
  <div className="mission-view-detailList">
    {items.map((item) => (
      <div className="mission-view-detailRow" key={item.label}>
        <span className="mission-view-detailLabel">{item.label}</span>
        <span className="mission-view-detailValue">{normalizeValue(item.value)}</span>
      </div>
    ))}
  </div>
);

const SectionCard = ({ number, title, subtitle, children, className = "" }) => (
  <section className={`mission-view-card ${className}`}>
    <div className="mission-view-cardHeader">
      <span className="mission-view-step">{number}</span>
      <div>
        <Typography className="mission-view-sectionTitle">{title}</Typography>
        {subtitle && <Typography className="mission-view-sectionSubtitle">{subtitle}</Typography>}
      </div>
    </div>
    {children}
  </section>
);

const StatPill = ({ icon, label, value }) => (
  <div className="mission-view-statPill">
    <span className="mission-view-statIcon">{icon}</span>
    <div>
      <span className="mission-view-statLabel">{label}</span>
      <strong>{normalizeValue(value)}</strong>
    </div>
  </div>
);

const RichTextBlock = ({ label, value }) => (
  <div className="mission-view-richBlock">
    <span className="mission-view-detailLabel">{label}</span>
    <div className="mission-view-richContent">
      <LabelHtmlToTextCustom data={value || "-"} />
    </div>
  </div>
);

const VisualiserMission = ({ history: historyProp, snackbarShowMessage }) => {
  const history = useHistory() || historyProp;
  const { id: societeId, missionId } = useParams();
  const [loading, setLoading] = useState(true);
  const [mission, setMission] = useState({});

  React.useEffect(() => {
    let isActive = true;
    (async () => {
      setLoading(true);
      try {
        const missionData = await getDetails(missionId);
        if (!isActive) return;
        setMission(missionData.data);
      } catch (e) {
        snackbarShowMessage("Erreur de chargement de la mission", "error", 4000);
      } finally {
        if (isActive) setLoading(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [missionId, snackbarShowMessage]);

  const editMission = () => {
    history.push(`/societe/${mission.societeId || societeId}/missions/${missionId}/update`, {
      numMission: mission.numMission,
    });
  };

  const activityDate = mission.updatedAt?.date
    ? `Modifiée le ${formatDate(mission.updatedAt.date)}`
    : mission.datecreation?.date
      ? `Créée le ${formatDate(mission.datecreation.date)}`
      : "";

  const missionItems = [
    { label: "Type de mission", value: mission.type },
    { label: "Nombre de postes", value: mission.nb_postes },
    { label: "Statut", value: mission.statut },
    { label: "Status", value: mission.status },
    { label: "Priorité", value: mission.priorite },
    { label: "À pourvoir sous", value: mission.pourvoir },
  ];

  const teamItems = [
    { label: "Talent Manager", value: mission.consultant },
    { label: "Delivery Manager", value: mission.accountManager },
    { label: "Sourceur", value: mission.sourceur },
    { label: "Destinataire principal", value: mission.contact },
    { label: "Destinataires secondaires", value: mission.secondaires },
  ];

  const profileItems = [
    { label: "Expérience", value: mission.experience },
    { label: "Formation", value: mission.formation },
    { label: "Compétences sectorielles", value: mission.secteur },
    { label: "Compétences métiers", value: mission.metier },
    { label: "Compétences techniques", value: mission.competence_tech },
    { label: "Niveau d'anglais", value: mission.niveau },
  ];

  return loading ? (
    <Loader />
  ) : (
    <Box className="mission-view-page">
      <div className="mission-view-shell">
        <div className="mission-view-toolbar">
          <IconButton className="mission-view-back" onClick={() => history.goBack()} title="Retour">
            <ArrowBack />
          </IconButton>
          <Button className="mission-view-edit" variant="contained" startIcon={<Edit />} onClick={editMission}>
            Modifier
          </Button>
        </div>

        <header className="mission-view-hero">
          <div className="mission-view-heroContent">
            <div className="mission-view-kicker">
              <BusinessCenter fontSize="small" />
              Mission N° {normalizeValue(mission.numMission)}
              {activityDate && <span>{activityDate}</span>}
            </div>
            <Typography className="mission-view-title">
              {normalizeValue(mission.nom_mission)}
            </Typography>
            <button
              className="mission-view-company"
              type="button"
              onClick={() => history.push(`/societe/${mission.societeId || societeId}/view`)}
            >
              {normalizeValue(mission.nom_societe)}
            </button>
            <div className="mission-view-chips">
              <Chip label={normalizeValue(mission.statut)} className="mission-view-chip mission-view-chipStatus" />
              <Chip label={`${normalizeValue(mission.nb_postes)} poste(s)`} className="mission-view-chip" />
              <Chip label={normalizeValue(mission.type)} className="mission-view-chip" />
            </div>
          </div>
          <div className="mission-view-stats">
            <StatPill icon={<Groups />} label="Postes" value={mission.nb_postes} />
            <StatPill icon={<Person />} label="Manager" value={mission.accountManager} />
            <StatPill icon={<Place />} label="Localisation" value={mission.localisation} />
            <StatPill icon={<Flag />} label="Priorité" value={mission.priorite} />
          </div>
        </header>

        <MissionAiProposals
          missionId={missionId}
          missionName={mission.nom_mission}
          societeName={mission.nom_societe}
        />

        <main className="mission-view-grid">
          <div className="mission-view-column">
            <SectionCard number="01" title="Informations mission" subtitle="Cadre, état et urgence du besoin.">
              <DetailList items={missionItems} />
            </SectionCard>

            <SectionCard number="03" title="Société et poste" subtitle="Contexte client et périmètre du poste.">
              <RichTextBlock label="Description" value={mission.description_societe} />
              <RichTextBlock label="Poste" value={mission.poste} />
            </SectionCard>
          </div>

          <div className="mission-view-column">
            <SectionCard number="02" title="Équipe et contacts" subtitle="Responsables et destinataires de la mission.">
              <DetailList items={teamItems} />
            </SectionCard>

            <SectionCard number="04" title="Critères de ciblage" subtitle="Profil recherché pour accélérer la sélection.">
              <DetailList items={profileItems} />
            </SectionCard>
          </div>
        </main>
      </div>
    </Box>
  );
};

export default withSnackbar(VisualiserMission);
